<template>
    <div class="VueCarousel">
        <div class="VueCarousel-wrapper">
            <div class="VueCarousel-inner" :style="innerStyle">
                <slot />
            </div>
        </div>

        <div v-if="navigationEnabled && slideCount > perPageCount" class="VueCarousel-navigation">
            <button
                type="button"
                class="VueCarousel-navigation-button VueCarousel-navigation-prev"
                :disabled="!loop && currentPage === 0"
                aria-label="Previous slide"
                @click="previous"
            >{{ navigationPrevLabel }}</button>
            <button
                type="button"
                class="VueCarousel-navigation-button VueCarousel-navigation-next"
                :disabled="!loop && currentPage === maxPage"
                aria-label="Next slide"
                @click="next"
            >{{ navigationNextLabel }}</button>
        </div>
    </div>
</template>

<script>
// Local carousel adapter that preserves the legacy <carousel>/<slide> usage.
export default {
    name: 'Carousel',
    props: {
        perPage: {
            type: Number,
            default: 1,
        },
        paginationEnabled: {
            type: Boolean,
            default: false,
        },
        navigationEnabled: {
            type: Boolean,
            default: false,
        },
        navigationNextLabel: {
            type: String,
            default: 'Next',
        },
        navigationPrevLabel: {
            type: String,
            default: 'Previous',
        },
        loop: {
            type: Boolean,
            default: false,
        },
        mouseDrag: {
            type: Boolean,
            default: true,
        },
        scrollPerPage: {
            type: Boolean,
            default: true,
        },
        navigateTo: {
            type: Number,
            default: 0,
        },
    },
    provide() {
        return {
            carousel: this,
        };
    },
    data() {
        return {
            currentPage: this.navigateTo || 0,
        };
    },
    computed: {
        perPageCount() {
            return Math.max(1, Number(this.perPage) || 1);
        },
        slideWidth() {
            return `${100 / this.perPageCount}%`;
        },
        slideVNodes() {
            const slot = this.$slots.default;
            if (!slot) {
                return [];
            }

            return this.getSlideVNodes(slot());
        },
        slideCount() {
            return this.slideVNodes.length;
        },
        maxSlideStart() {
            return Math.max(0, this.slideCount - this.perPageCount);
        },
        maxPage() {
            if (!this.scrollPerPage) {
                return this.maxSlideStart;
            }

            return Math.max(0, Math.ceil(this.slideCount / this.perPageCount) - 1);
        },
        currentSlideStart() {
            if (!this.scrollPerPage) {
                return Math.min(this.currentPage, this.maxSlideStart);
            }

            return Math.min(this.currentPage * this.perPageCount, this.maxSlideStart);
        },
        innerStyle() {
            return {
                display: 'flex',
                transform: `translateX(-${this.currentSlideStart * (100 / this.perPageCount)}%)`,
                transition: 'transform 300ms ease',
                width: '100%',
            };
        },
    },
    watch: {
        navigateTo(value) {
            this.currentPage = Math.min(Math.max(Number(value) || 0, 0), this.maxPage);
        },
        maxPage(value) {
            if (this.currentPage > value) {
                this.currentPage = value;
            }
        },
    },
    methods: {
        getSlideVNodes(nodes) {
            return nodes.reduce((slides, node) => {
                const componentName = node.type && node.type.name;

                if (componentName === 'CarouselSlide') {
                    slides.push(node);
                    return slides;
                }

                if (Array.isArray(node.children)) {
                    slides.push(...this.getSlideVNodes(node.children));
                }

                return slides;
            }, []);
        },
        previous() {
            if (this.currentPage <= 0) {
                this.currentPage = this.loop ? this.maxPage : 0;
                return;
            }

            this.currentPage -= 1;
        },
        next() {
            if (this.currentPage >= this.maxPage) {
                this.currentPage = this.loop ? 0 : this.maxPage;
                return;
            }

            this.currentPage += 1;
        },
    },
};
</script>

<style scoped>
.VueCarousel {
    position: relative;
    width: 100%;
}

.VueCarousel-wrapper {
    overflow: hidden;
    position: relative;
    width: 100%;
}

.VueCarousel-inner {
    backface-visibility: hidden;
    flex-direction: row;
}

.VueCarousel-navigation-button {
    appearance: none;
    border: none;
    box-sizing: border-box;
    outline: none;
    position: absolute;
    text-decoration: none;
    top: 50%;
    cursor: pointer;
}

.VueCarousel-navigation-prev {
    left: 0;
}

.VueCarousel-navigation-next {
    right: 0;
}

.VueCarousel-navigation-button:disabled {
    cursor: default;
    opacity: 0.4;
}
</style>
